import React, { useEffect, useRef, useState } from 'react';
import {
  FiAlertCircle,
  FiAlertTriangle,
  FiCheckCircle,
  FiInfo,
  FiLoader,
  FiX,
} from 'react-icons/fi';
import './StatusTray.css';

const iconByType = {
  success: FiCheckCircle,
  error: FiAlertCircle,
  warning: FiAlertTriangle,
  info: FiInfo,
  loading: FiLoader,
};

const handleStatusActionClick = (event, href) => {
  if (!href || typeof window === 'undefined') return;

  const targetUrl = new URL(href, window.location.origin);
  if (targetUrl.origin !== window.location.origin) return;

  event.preventDefault();
  window.history.pushState({}, '', `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`);
  const navEvent = typeof PopStateEvent === 'function'
    ? new PopStateEvent('popstate')
    : new Event('popstate');
  window.dispatchEvent(navEvent);
};

const StatusItem = ({ status, onDismiss }) => {
  const [paused, setPaused] = useState(false);
  const remainingRef = useRef(status.durationMs);
  const startedAtRef = useRef(null);
  const timeoutRef = useRef(null);
  const Icon = iconByType[status.type] || FiInfo;
  const canAutoDismiss = Number.isFinite(status.durationMs) && status.durationMs > 0;

  useEffect(() => {
    remainingRef.current = status.durationMs;
    startedAtRef.current = null;
  }, [status.createdAt, status.durationMs]);

  useEffect(() => {
    if (!canAutoDismiss || paused) {
      return undefined;
    }

    startedAtRef.current = Date.now();
    timeoutRef.current = window.setTimeout(() => {
      onDismiss(status.id);
    }, Math.max(0, remainingRef.current));

    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (startedAtRef.current) {
        remainingRef.current = Math.max(0, remainingRef.current - (Date.now() - startedAtRef.current));
        startedAtRef.current = null;
      }
    };
  }, [canAutoDismiss, onDismiss, paused, status.id, status.createdAt]);

  const pause = () => setPaused(true);
  const resume = () => setPaused(false);

  return (
    <article
      className={`status-toast status-toast--${status.type}`}
      role={status.type === 'error' || status.type === 'warning' ? 'alert' : 'status'}
      aria-live={status.type === 'error' || status.type === 'warning' ? 'assertive' : 'polite'}
      onMouseEnter={pause}
      onMouseLeave={resume}
      onFocus={pause}
      onBlur={resume}
    >
      <span className="status-toast-icon" aria-hidden="true">
        <Icon className={status.type === 'loading' ? 'status-toast-spinner' : ''} />
      </span>
      <div className="status-toast-content">
        <div className="status-toast-title-row">
          <strong className="status-toast-title">{status.title}</strong>
          {status.source ? <span className="status-toast-source">{status.source}</span> : null}
        </div>
        {status.message ? <p className="status-toast-message">{status.message}</p> : null}
        {status.actionHref && status.actionLabel ? (
          <a
            className="status-toast-action"
            href={status.actionHref}
            onClick={(event) => {
              handleStatusActionClick(event, status.actionHref);
              onDismiss(status.id);
            }}
          >
            {status.actionLabel}
          </a>
        ) : null}
      </div>
      <button
        type="button"
        className="status-toast-dismiss"
        onClick={() => onDismiss(status.id)}
        aria-label="Lukk status"
      >
        <FiX aria-hidden="true" />
      </button>
    </article>
  );
};

const StatusTray = ({ statuses = [], onDismiss }) => {
  if (!statuses.length) return null;

  return (
    <div className="status-tray" aria-label="Statusmeldinger">
      {statuses.map((status) => (
        <StatusItem key={status.id} status={status} onDismiss={onDismiss} />
      ))}
    </div>
  );
};

export default StatusTray;
